import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from "react-markdown";
import { 
  Send, RotateCcw, X, Smile, Paperclip, ChevronDown, 
  MessageSquare, Search, AlertCircle
} from 'lucide-react';
const BASE_URL = process.env.REACT_APP_BASE_URL;
const App = () => {
  const [messages, setMessages] = useState([
    { 
      id: 1, 
      text: "👋 Hi! I'm your Hospital Claim Bot.\nPlease select Corporate or Private patient type!", 
      sender: 'bot',
      step: 1
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [serverStatus, setServerStatus] = useState('checking');
  const [sessionId] = useState(`session_${Date.now()}`);
  const [currentStep, setCurrentStep] = useState(1);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetch(`${BASE_URL}/`)
      .then(() => setServerStatus('online'))
      .catch(() => setServerStatus('offline'));
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);
  const getQuickActionButtons = (step) => {
    const buttons = [];
    
    switch(step) {
      case 1:
        buttons.push(
          { text: "🏢 Corporate", action: "Corporate", filled: true },
          { text: "🏠 Private", action: "Private", filled: true }
        );
        break;
      case 2:
      
        buttons.push(
          { text: "✅ Yes", action: "Yes", filled: true },
          { text: "❌ No", action: "No", filled: true }
        );
        break;
      case 5:
        buttons.push(
          { text: "🔄 New Search", action: "yes", filled: true },
          { text: "📦 Dispatch Detail", action: "Dispatch Detail", filled: true },
          { text: "🏢 Settlement Details", action: "Settlement Details", filled: true },
          { text: "📄 Query", action: "Query", filled: true }
        );
        break;
      default:
        break;
    }
    
    return buttons;
  };


  const handleSendMessage = async (customText = null) => {
    const textToSend = customText || input;
    if (!textToSend.trim() || loading) return;

    const userMsg = { 
      id: Date.now(), 
      text: textToSend, 
      sender: 'user'
    };
    
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch(`${BASE_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          query: textToSend, 
          session_id: sessionId 
        }),
      });
      
      if (!response.ok) throw new Error('API Error');
      const data = await response.json();
      
      const botMessages = data.responses.map((resText, index) => ({
        id: Date.now() + index + 1,
        text: resText,
        sender: 'bot',
        step: data.step
      }));
      
      setMessages(prev => [...prev, ...botMessages]);
      setCurrentStep(data.step);
    } catch (error) {
      setMessages(prev => [...prev, { 
        id: Date.now(), 
        text: `❌ Error: ${serverStatus === 'offline' 
          ? 'FastAPI server not running on port 8000' 
          : 'Database connection failed. Check MySQL' }`, 
        sender: 'bot' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const currentButtons = getQuickActionButtons(currentStep);

  return (
    <div className="bot-container">
      <style>{`
        * { margin: 0; padding: 0; box-sizing: border-box; }
        .bot-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-family: 'Inter', -apple-system, sans-serif;
          padding: 20px;
        }
        .chat-widget {
          width: 420px;
          height: 680px;
          background: white;
          border-radius: 24px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          position: relative;
        }
        .server-status {
          position: absolute;
          top: 10px;
          right: 10px;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          z-index: 10;
        }
        .status-online { background: #dcfce7; color: #166534; }
        .status-offline { background: #fee2e2; color: #dc2626; }
        .chat-header {
          padding: 24px 20px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid #f8fafc;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        }
        .header-info { display: flex; align-items: center; gap: 12px; }
        .bot-icon {
          width: 44px; height: 44px;
          background: linear-gradient(135deg, #6E2CF4, #A855F7);
          border-radius: 16px;
          display: flex; align-items: center; justify-content: center;
          color: white; font-weight: bold; font-size: 18px;
        }
        .online-dot { 
          width: 10px; height: 10px; 
          background: #22c55e; 
          border-radius: 50%; 
          box-shadow: 0 0 0 2px white;
          animation: pulse 2s infinite;
        }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .chat-main {
          flex: 1; overflow-y: auto; padding: 24px 20px;
          background: #f8fafc;
          display: flex; flex-direction: column; gap: 16px;
          scrollbar-width: thin;
        }
        .chat-main::-webkit-scrollbar { width: 4px; }
        .chat-main::-webkit-scrollbar-track { background: transparent; }
        .chat-main::-webkit-scrollbar-thumb { 
          background: #e2e8f0; border-radius: 2px; 
        }
        .msg-bubble {
          
          max-width: 85%; padding: 14px 18px; 
          border-radius: 20px; font-size: 14px;
          line-height: 1.5; word-wrap: break-word;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          white-space: pre-wrap;
        }
        .msg-bot { 
          background: white; 
          border-top-left-radius: 4px; 
          color: #475569; 
          border: 1px solid #e2e8f0;
          align-self: flex-start; 
        }
        .msg-user { 
          background: linear-gradient(135deg, #6E2CF4, #A855F7);
          color: white; 
          border-top-right-radius: 4px; 
          align-self: flex-end;
          font-weight: 500;
        }
        .chat-footer { 
          padding: 20px; 
          border-top: 1px solid #f1f5f9; 
          background: white;
        }
        .input-wrapper {
          background: #f8fafc; 
          border-radius: 20px; 
          display: flex; 
          padding: 8px 12px; 
          align-items: center;
          border: 2px solid transparent;
          transition: border-color 0.2s;
        }
        .input-wrapper:focus-within { border-color: #6E2CF4; }
        .chat-input {
          flex: 1; background: transparent; 
          border: none; padding: 8px 0; 
          outline: none; font-size: 14px;
          color: #475569;
        }
        .send-btn {
          background: linear-gradient(135deg, #6E2CF4, #A855F7);
          color: white; border: none; 
          border-radius: 14px; padding: 10px;
          cursor: pointer; display: flex; 
          align-items: center; justify-content: center;
          width: 40px; height: 40px;
          transition: transform 0.2s;
        }
        .send-btn:hover:not(:disabled) { transform: scale(1.05); }
        .send-btn:disabled { background: #cbd5e1; cursor: not-allowed; }
        
        /* 🎨 UPDATED QUICK ACTION BUTTONS */
        .suggest-btn {
          background: #f8fafc !important; 
          border: 1px solid #e2e8f0 !important; 
          color: #64748b !important;
          padding: 10px 18px !important; 
          border-radius: 20px !important; 
          font-size: 13px !important; 
          font-weight: 600 !important;
          cursor: pointer !important; 
          align-self: flex-start !important;
          margin: 6px !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
          position: relative !important;
          overflow: hidden !important;
        }
        
        .suggest-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          transition: left 0.5s;
        }
        
        .suggest-btn:hover {
          background: linear-gradient(135deg, #f1f5f9, #e2e8f0) !important;
          transform: translateY(-2px) scale(1.02) !important;
          box-shadow: 0 8px 25px rgba(110, 44, 244, 0.15) !important;
          border-color: #c4b5fd !important;
          color: #3730a3 !important;
        }
        
        .suggest-btn:hover::before {
          left: 100%;
        }
        
        .suggest-btn-filled { 
          background: linear-gradient(135deg, #e0d9ff, #c4b5fd) !important;
          color: #6b46c1 !important; 
          border: 1px solid #a78bfa !important;
          box-shadow: 0 4px 12px rgba(110, 44, 244, 0.2) !important;
        }
        
        .suggest-btn-filled:hover { 
          background: linear-gradient(135deg, #c4b5fd, #a78bfa) !important;
          transform: translateY(-2px) scale(1.02) !important;
          box-shadow: 0 12px 30px rgba(110, 44, 244, 0.3) !important;
          color: #5b21b6 !important;
          border-color: #8b5cf6 !important;
        }
        
        .buttons-row {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 12px;
          padding: 12px 0;
        }
        .footer-info {
          display: flex; 
          align-items: center; 
          margin-top: 16px; 
          padding-top: 12px;
          border-top: 1px solid #f1f5f9;
          gap: 12px;
        }
        .footer-icons { display: flex; gap: 8px; color: #94a3b8; }
      `}</style>

      <div className="chat-widget">
        <div className={`server-status status-${serverStatus}`}>
          <AlertCircle size={12} /> {serverStatus.toUpperCase()}
        </div>
        
        <header className="chat-header">
          <div className="header-info">
            <div className="bot-icon">🏥</div>
            <div>
              <div style={{fontWeight:'bold', fontSize:'16px', color:'#1e293b'}}>ClaimCare Bot</div>
              <div style={{display:'flex', alignItems:'center', gap:'6px'}}>
                <div className="online-dot"></div>
                <span style={{fontSize:'11px', color:'#64748b'}}>Multi-Step Flow</span>
              </div>
            </div>
          </div>
          <div style={{display:'flex', gap:'12px', color:'#94a3b8'}}>
            <RotateCcw size={18} style={{cursor:'pointer'}} 
              onClick={() => {
                setMessages([messages[0]]);
                setCurrentStep(1);
                localStorage.removeItem(sessionId);
              }} />
            <X size={18} style={{cursor:'pointer'}} />
          </div>
        </header>
        
        <main ref={scrollRef} className="chat-main">
          {messages.map(msg => (
            <div key={msg.id} className={`msg-bubble ${msg.sender === 'user' ? 'msg-user' : 'msg-bot'}`}>

              <ReactMarkdown
                
                components={{
                  ul: ({node, ...props}) => (
                    <ul style={{ paddingLeft: '18px', margin: '8px 0' }} {...props} />
                  ),
                  li: ({node, ...props}) => (
                    <li style={{ marginBottom: '4px' }} {...props} />
                  ),
                  a: ({ node, ...props }) => (
                    <a 
                      {...props} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#6E2CF4', fontWeight: '600' }}
                    />
                  )
                }}
              >
                {msg.text.replace(/\n/g, "  \n")}
              </ReactMarkdown>
              {/* <ReactMarkdown
                components={{
                  a: ({ node, ...props }) => (
                    <a 
                      {...props} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#6E2CF4', fontWeight: '600' }}
                    />
                  )
                }}
              >
                {msg.text.replace(/\n/g, "  \n")}
              </ReactMarkdown> */}

              {/* <ReactMarkdown
                components={{
                  a: ({ node, ...props }) => (
                    <a 
                      {...props} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#6E2CF4', fontWeight: '600' }}
                    />
                  )
                }}
              >
                {msg.text}
              </ReactMarkdown> */}


            </div>
          ))}
          {loading && (
            <div className="msg-bubble msg-bot">
              <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                <div style={{width:'16px', height:'16px', border:'2px solid #e2e8f0', 
                  borderTop:'2px solid #6E2CF4', borderRadius:'50%', 
                  animation:'spin 1s linear infinite'}}></div>
                Processing...
              </div>
            </div>
          )}
          
          {/* Dynamic Quick Action Buttons */}
          {currentButtons.length > 0 && !loading && (
            <div className="buttons-row">
              {currentButtons.map((btn, index) => (
                <button 
                  key={index}
                  className={`suggest-btn ${btn.filled ? 'suggest-btn-filled' : ''}`} 
                  onClick={() => handleSendMessage(btn.action)}
                >
                  {btn.text}
                </button>
              ))}
            </div>
          )}
        </main>

        <footer className="chat-footer">
          <div className="input-wrapper">
            <input 
              className="chat-input"
              placeholder={
                currentStep === 1 ? "Type Corporate/Private/Yes/No/Claim ID/Name/Phone..." :
                currentStep === 2 || currentStep === 2.1 ? "Type Yes/No..." :
                currentStep === 3 ? "Enter Claim Number..." :
                currentStep === 2.2 ? "Enter Employee ID..." :
                currentStep === 4 ? "Enter patient name..." :
                currentStep === 4.1 ? "Enter Phone Number..." :
                "Type your message..."
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && !loading && handleSendMessage()}
            />
            <button 
              className="send-btn" 
              onClick={() => handleSendMessage()} 
              disabled={!input.trim() || loading}
            >
              <Send size={18} />
            </button>
          </div>
          <div className="footer-info">
            <div className="footer-icons">
              <Smile size={18} />
              <Paperclip size={18} />
            </div>
            <div style={{marginLeft:'auto', fontSize:'11px', color:'#94a3b8'}}>
              FastAPI + MySQL + React
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default App;
